import Box from "@mui/material/Box";
import {
  DataGridPro,
  GRID_TREE_DATA_GROUPING_FIELD,
  GridToolbarFilterButton,
  GridTreeDataGroupingCell,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useContext, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  setClient,
  setClientID,
  setProjectID,
} from "../../../Redux/Actions/actions";
import { NoRowsOverlay } from "../Client/DataGridCustomComponents/clientComponents";
import { DashboardContext } from "../DashboardContext/DashboardContextProvider";
import * as defaultStyles from "../styles/main-datagrid.module.css";
import { CustomGridPagination } from "../UniversalDataGridHelper/dataGridComponents";
import useAdminRows from "./AdminQueries/useAdminRows";
import { useArchiveProjects } from "./AdminQueries/useArchiveProjects";
import useInviteUserToProject from "./AdminQueries/useEmailClient";
import { CustomToolbar } from "./Helper/adminComponents";
import { getColumns } from "./Helper/adminDataGridHelper";
import "./styles/admin-portal.css";
import "./styles/admin-requests.css";

const AdminRequests = () => {
  const {
    hideArchived,
    isAdmin,
    navigate,
    queryClient,
    setOpenEmail,
    currEmailRow,
    emailAnchor,
    openEmailPopoverHandler,
    servicesAnchor,
    setOpenServices,
    currServicesRow,
    openServicesPopoverHandler,
    contactsAnchor,
    setOpenContacts,
    currContactsRow,
    openContactsPopoverHandler,
  } = useContext(DashboardContext);

  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  let initialRows = useRef([]);
  const [rows, setRows] = useState([]);
  const [searchValue, setSearchValue] = useState("");

  const { enqueueSnackbar } = useSnackbar();

  // Query Row Data
  const { data: rowData, isFetched, isLoading } = useAdminRows(isAdmin);

  // Set initial rows to the fetched data
  useEffect(() => {
    if (isFetched && rowData) {
      setRows(() => []);
      initialRows.current = [];
      initialRows.current.push(...rowData);
      if (hideArchived) {
        setRows(() => initialRows.current.filter((row) => !row.archived));
      } else {
        setRows(() => [...initialRows.current]);
      }
    }
  }, [isFetched, rowData, hideArchived]);

  const checkSrf = async (clientId, projectId) => {
    try {
      const res = await axios.get(`/api/check-srf/${clientId}/${projectId}/`, {
        withCredentials: true,
      });

      console.log("Check SRF Response: ", res);

      return res.status;
    } catch (error) {
      console.error("Error checking srf: ", error);
    }
  };

  // Mutation for regenerating srf
  const updateSrf = useMutation({
    mutationFn: async (data) => {
      console.log(`Client: ${data.clientId}, Project: ${data.projectId}`);
      const res = await axios.patch(
        `/api/srf/${data.clientId}/${data.projectId}/`,
        {},
        {
          withCredentials: true,
        },
      );

      return res.data;
    },
    onError: (error, variables, context) => {
      console.log("UPDATE SRF ERROR: ", error);
    },
    onSettled: (data, error, variables, context) => {
      console.log("UPDATE SRF SETTLED DATA: ", data);
      console.log("UPDATE SRF SETTLED VARIABLES: ", variables);
      queryClient.invalidateQueries({
        queryKey: ["srf", variables[0], variables[1]],
      });
    },
  });

  useEffect(() => {
    console.log("ROWS: ", rows);
  }, [rows]);

  // Mutation for adding/removing archived projects (only if requestStatus is "C")
  const updateArchive = useArchiveProjects(enqueueSnackbar, rows, setRows);

  // Mutation for sending email invitation to client user for a specific project
  const { mutateAsync: asyncSendEmail } =
    useInviteUserToProject(enqueueSnackbar);

  // Props for popover components in the data grid
  const servicesPopoverProps = {
    servicesAnchor,
    currServicesRow,
    openServicesPopoverHandler,
  };

  const contactsPopoverProps = {
    contactsAnchor,
    currContactsRow,
    openContactsPopoverHandler,
  };

  const emailUserProps = {
    emailAnchor,
    currEmailRow,
    openEmailPopoverHandler,
    asyncSendEmail,
  };

  // Set the open state of the popovers
  useEffect(() => {
    setOpenEmail(Boolean(emailAnchor));
  }, [emailAnchor]);

  useEffect(() => {
    setOpenServices(Boolean(servicesAnchor));
  }, [servicesAnchor]);

  useEffect(() => {
    setOpenContacts(Boolean(contactsAnchor));
  }, [contactsAnchor]);

  // Group/Tree Data column definition
  const getTreeDataPath = (row) => row.hierarchy;
  const groupColDef = {
    headerName: "Client",
    flex: 0.8,
    headerClassName: defaultStyles?.groupColHeader,
    headerAlign: "center",
    minWidth: 100,
    sortable: true,
    resizable: false,
    cellClassName: (params) => {
      return params.rowNode.type === "group"
        ? defaultStyles.groupColCell
        : defaultStyles.groupColEmpty;
    },
    renderCell: (params) => {
      return params.rowNode.type === "group" ||
        (params.rowNode.type === "leaf" &&
          params.rowNode.parent === "auto-generated-group-node-root") ? (
        <GridTreeDataGroupingCell
          {...params}
          hideDescendantCount
          offsetMultiplier={5}
        />
      ) : null;
    },
  };

  // SEARCH BAR LOGIC
  const handleSearch = (e) => {
    let value = e.target.value.trimStart().toLowerCase();
    setSearchValue(value);

    const statusConversion = {
      P: "Pending",
      IP: "In Progress",
      I: "Editable",
      RV: "Revisions",
      C: "Completed",
      A: "Amending",
    };

    let dataToFilter = hideArchived
      ? initialRows.current.filter((row) => !row.archived)
      : initialRows.current;

    let currentFilteredRows = dataToFilter.filter((row) => {
      let rowValues = [
        row.clientName,
        row.projectName,
        row.requestType,
        statusConversion[row.requestStatus],
      ];

      if (row.projectCity) {
        rowValues.push(row.projectCity);
      }
      if (row.services["swppp"].length > 0) {
        rowValues = [...rowValues, ...row.services["swppp"]];
      }
      if (row.services["airQuality"].length > 0) {
        rowValues = [...rowValues, ...row.services["airQuality"]];
      }
      let rowValuesString = rowValues.join(" ").toLowerCase();

      return rowValuesString.includes(value.toLowerCase());
    });
    setRows([...currentFilteredRows]);
  };

  // CLEAR INPUT LOGIC
  const handleClearInput = (
    e,
    setSearchValue,
    setRows,
    initialRows,
    hideArchived,
  ) => {
    setSearchValue("");
    if (hideArchived === true) {
      setRows([...initialRows.current.filter((row) => !row.archived)]);
    } else {
      setRows(initialRows.current);
    }
  };

  return (
    <Box
      id="admin-grid-container"
      component={"div"}
      className={rows.length > 0 ? "rows" : "no-rows"}
    >
      <h1 id="grid-header">Clients Table</h1>
      <DataGridPro
        rows={rows}
        columns={getColumns(
          dispatch,
          navigate,
          setClient,
          setClientID,
          setProjectID,
          queryClient,
          updateSrf,
          emailUserProps,
          servicesPopoverProps,
          contactsPopoverProps,
          updateArchive,
          checkSrf,
        )}
        apiRef={apiRef}
        editMode="row"
        getRowHeight={() => "auto"}
        pageSizeOptions={[1, 2, 5, 8, 10]}
        pagination
        pinnedColumns={{ left: [GRID_TREE_DATA_GROUPING_FIELD] }}
        loading={isLoading}
        slots={{
          toolbar: CustomToolbar,
          gridToolbarFilterButton: GridToolbarFilterButton,
          pagination: CustomGridPagination,
          noRowsOverlay: NoRowsOverlay,
        }}
        slotProps={{
          loadingOverlay: {
            variant: "circular-progress",
            norowsvariant: "circular-progress",
            sx: {
              backgroundColor: "transparent",
              "& .MuiCircularProgress-svg": {
                color: "var(--argus-white)",
                fill: "var(--argus-white)",
              },
            },
          },
          toolbar: {
            value: searchValue,
            handleSearch: handleSearch,
            rows: rows,
            setRows: setRows,
            rowData: rowData,
            setSearchValue: setSearchValue,
            handleClearInput: handleClearInput,
            initialRows: initialRows,
          },
          pagination: {
            labelRowsPerPage: "Clients per page:",
            labelDisplayedRows: ({ from, to, count }) =>
              `Clients ${from}-${to} of ${
                count !== -1 ? count : `more than ${to}`
              }`,
            className: defaultStyles["main-grid-pagination"],
            id: defaultStyles["admin-grid-pagination"],
            classes: {
              root: defaultStyles["main-grid-pagination-root"],
              displayedRows: defaultStyles["pagination-displayedRows"],
              select: defaultStyles["pagination-select"],
              selectLabel: defaultStyles["pagination-selectLabel"],
              input: defaultStyles["pagination-select-input"],
              toolbar: defaultStyles["grid-pagination-toolbar"],
            },
          },
          filterPanel: {
            className: defaultStyles["filter-panel"],
            filterFormProps: {
              className: defaultStyles["filter-form"],
              columnInputProps: {
                className: defaultStyles["filter-column-inputs"],
                sx: {
                  "& .MuiFormLabel-root": {
                    color: "var(--label-color)",
                    fontFamily: "var(--default-font)",
                    fontWeight: "var(--default-font-weight)",
                  },
                  "& .MuiInputBase-root": {
                    color: "var(--argus-white)",
                    fontFamily: "var(--default-font)",
                    fontWeight: "var(--default-font-weight)",
                    borderBottom: "none !important",
                    "&::after": {
                      borderBottom: "none !important",
                    },
                    "&::before": {
                      borderBottom: "none !important",
                    },
                    "& .MuiNativeSelect-select": {
                      borderBottom: "2px solid var(--argus-white) !important",
                      "&::before": {
                        borderBottom: "none !important",
                      },
                      "&::after": {
                        borderBottom: "none !important",
                      },
                    },
                    "& .MuiSvgIcon-root": {
                      color: "var(--argus-white)",
                    },
                  },
                },
              },
              operatorInputProps: {
                className: defaultStyles["operator-column-inputs"],
                sx: {
                  "& .MuiFormLabel-root": {
                    color: "var(--label-color)",
                    fontFamily: "var(--default-font)",
                    fontWeight: "var(--default-font-weight)",
                  },
                  "& .MuiInputBase-root": {
                    color: "var(--argus-white)",
                    fontFamily: "var(--default-font)",
                    fontWeight: "var(--default-font-weight)",
                    borderBottom: "none !important",
                    "&::after": {
                      borderBottom: "none !important",
                    },
                    "&::before": {
                      borderBottom: "none !important",
                    },
                    "& .MuiNativeSelect-select": {
                      borderBottom: "2px solid var(--argus-white) !important",
                      "&::before": {
                        borderBottom: "none !important",
                      },
                      "&::after": {
                        borderBottom: "none !important",
                      },
                    },
                    "& .MuiSvgIcon-root": {
                      color: "var(--argus-white)",
                    },
                  },
                },
              },
              valueInputProps: {
                className: defaultStyles["value-column-inputs"],
                id: "value-column-inputs",
                InputComponentProps: {
                  className: defaultStyles["value-input"],
                  id: defaultStyles["value-input"],
                  style: {
                    color: "var(--argus-white)",
                    fontFamily: "var(--default-font)",
                    fontWeight: "var(--default-font-weight)",
                  },
                },
                sx: {
                  "& .MuiFormLabel-root": {
                    color: "var(--label-color)",
                    fontFamily: "var(--default-font)",
                    fontWeight: "var(--default-font-weight)",
                  },
                  "& .MuiInputBase-root": {
                    color: "var(--argus-white) !important",
                    fontFamily: "var(--default-font)",
                    fontWeight: "var(--default-font-weight)",
                    "&::after": {
                      borderBottom: "none !important",
                    },
                    "&::before": {
                      borderBottom: "none !important",
                    },
                    "& .MuiInputBase-input": {
                      borderBottom: "2px solid var(--argus-white) !important",
                      color: "var(--argus-white)",
                      "&::before": {
                        borderBottom: "none !important",
                      },
                      "&::after": {
                        borderBottom: "none !important",
                      },
                    },
                  },
                },
              },
              logicalOperatorInputProps: {
                color: "var(--argus-white)",
                fontFamily: "var(--default-font)",
                fontWeight: "var(--default-font-weight)",
              },
              deleteIconProps: {
                sx: {
                  "& .MuiSvgIcon-root": {
                    color: "var(--close-icon-color)",
                    stroke: "var(--close-icon-color)",
                  },
                },
              },
              panelFooterProps: {
                className: defaultStyles["filter-panel-footer"],
              },
            },
          },
        }}
        treeData
        defaultGroupingExpansionDepth={1}
        classes={{
          footerContainer: defaultStyles.footerContainer,
          columnHeader: defaultStyles?.columnHeader,
          columnHeaders: defaultStyles["columnHeaders"],
          columnHeadersInner: defaultStyles?.columnHeadersInner,
          "columnHeader--sorted": defaultStyles?.columnHeaderSorted,
          "columnHeader--sortable": defaultStyles?.columnHeaderSortable,
          columnHeaderDraggableContainer:
            defaultStyles?.columnHeaderDraggableContainer,
          columnHeaderTitle: defaultStyles?.columnHeaderTitle,
          columnHeaderTitleContainer: defaultStyles?.columnHeaderTitleContainer,
          columnSeparator: defaultStyles?.columnSeparator,
          sortIcon: defaultStyles?.sortIcon,
          columnHeaderCheckbox: defaultStyles?.columnHeaderCheckbox,
          iconButtonContainer: defaultStyles?.iconButtonContainer,
          cell: defaultStyles?.cell,
          cellContent: defaultStyles?.cellContent,
          cellSkeleton: defaultStyles?.skeletonCell,
          panelFooter: defaultStyles["filter-panel-footer"],
          pinnedColumns: defaultStyles?.pinnedColumns,
          pinnedColumnHeaders: defaultStyles?.pinnedColumnHeaders,
          treeDataGroupingCell: defaultStyles?.treeDataGroupingCell,
          treeDataGroupingCellToggle: defaultStyles?.treeDataGroupingCellToggle,
          "row--editing": defaultStyles?.editingRow,
          virtualScroller: defaultStyles?.virtualScroller,
          virtualScrollerRenderZone: defaultStyles?.renderZone,
          virtualScrollerContent: defaultStyles?.virtualScrollerContent,
          overlayWrapper: defaultStyles?.overlayWrapper,
          overlayWrapperInner: defaultStyles?.overlayWrapperInner,
          row: defaultStyles?.row,
          actionsCell: defaultStyles?.actionsCellContainer,
        }}
        sx={[
          {
            boxShadow: 7,
            border: 3,
            borderColor: "var(--outline-color)",
            fontFamily: "var(--default-font) !important",
            color: "var(--argus-white)",
            backgroundColor: "var(--main-blue) !important",
            width: "100%",
            minWidth: "95% !important",
            "& .MuiDataGrid-container--top [role=row]": {
              background: "transparent !important",
            },
            "& .MuiDataGrid-row--borderBottom": {
              borderBottom: "2px solid var(--outline-color)",
              overflow: "hidden",
              background: "transparent !important",
            },
            "& .MuiDataGrid-row": {
              minHeight: "50px",
              maxHeight: "60px",
            },
            "& .MuiDataGrid-actionsCell": {
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "0px",
              margin: "0px",
              width: "100%",
              height: "100%",
              gap: "5px",
              // backgroundColor: "black !important",
              ".MuiIconButton-root": {
                color: "rgb(225, 240, 240)",
                minHeight: "30px !important",
                height: "100% !important",
                minWidth: "fit-content !important",
                width: "fit-content !important",
                margin: "0px",
                padding: "0px",
                "&:hover": {
                  backgroundColor: "transparent",
                },
                "& .MuiSvgIcon-root": {
                  fontSize: "clamp(18px, 1.3cqw, 22px)",
                  stroke: "var(--argus-white)",
                  strokeWidth: "0.2px",
                  minHeight: "fit-content",
                  minWidth: "fit-content",
                  padding: "0px",
                  margin: "0px",
                },
              },
            },
          },
        ]}
        showCellVerticalBorders={true}
        disableRowSelectionOnClick
        columnHeaderHeight={65}
        showColumnBorders={true}
        groupingColDef={groupColDef}
        getTreeDataPath={getTreeDataPath}
        initialState={{
          pagination: { paginationModel: { pageSize: 5 } },
        }}
        columnVisibilityModel={{
          savedAt: false,
        }}
        isCellEditable={(params) =>
          params.field === "archived" && params.row.requestStatus === "C"
        }
        rowThreshold={0}
      />
    </Box>
  );
};

export default AdminRequests;
